import React, { useEffect, useState } from 'react';
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Cookies from 'js-cookie';
import BackButton from '../backButton';
import { APIUpdatePromoCode, APIGetOperator } from '../../utils/apiCalled';
import { usePrivilege } from '../../PrivilegeContext';

export default function PagePromoCode({
  promoCode,
  setOpen,
  setSuccMsg,
  setErrMsg,
  setVariant,
  setMainMsg,
  setModalTriggerAPICalled,
  handleCloseMorePromoCodes,
}) {
  const { role } = usePrivilege();
  const theme = useTheme();

  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [validityPeriodDays, setValidityPeriodDays] = useState('');
  const [applicationLimit, setApplicationLimit] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState('');
  const [applicableZonesText, setApplicableZonesText] = useState('');
  const [assignedOperators, setAssignedOperators] = useState([]);
  const [operatorsList, setOperatorsList] = useState([]);
  const [errors, setErrors] = useState({});
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  useEffect(() => {
    if (promoCode) {
      setCode(promoCode.code || '');
      setDescription(promoCode.description || '');
      setType(promoCode.type || '');
      setStatus(promoCode.status || 'ACTIVE');
      setValidityPeriodDays(promoCode.validityPeriodDays != null ? promoCode.validityPeriodDays.toString() : '');
      setApplicationLimit(promoCode.applicationLimit != null ? promoCode.applicationLimit.toString() : '');
      setDiscountPercentage(promoCode.discountPercentage != null ? promoCode.discountPercentage.toString() : '');
      const zonesArray = Array.isArray(promoCode.applicableZones) ? promoCode.applicableZones : [];
      setApplicableZonesText(zonesArray.join(', '));
      // Set assignedOperators based on role
      if (role === 101 || role === 900) {
        // For super admin (101/900), use promoCode's assignedOperators
        if (promoCode.assignedOperators) {
          setAssignedOperators(Array.isArray(promoCode.assignedOperators)
            ? promoCode.assignedOperators
            : [promoCode.assignedOperators]);
        } else {
          setAssignedOperators([]);
        }
      } else {
        // For non-super admin, use selectedOperatorId from cookies
        const operatorAssigned = Cookies.get('selectedOperatorId');
        if (operatorAssigned) {
          const assignedIds = operatorAssigned.split(',').map((id) => id.trim());
          setAssignedOperators(assignedIds);
        } else {
          setAssignedOperators([]);
        }
      }
    }
  }, [promoCode, role]);

  useEffect(() => {
    const fetchOperators = async () => {
      try {
        const response = await APIGetOperator(() => {});
        if (response.status === 200) {
          if (role === 101 || role === 900) {
            setOperatorsList(response.data);
          } else {
            const operatorAssigned = Cookies.get('selectedOperatorId');
            const assignedIds = operatorAssigned
              ? operatorAssigned.split(',').map((id) => id.trim())
              : [];
            const matchedOperators = response.data.filter((op) => (
              assignedIds.includes(op._id.toString())
            ));
            setOperatorsList(matchedOperators);
          }
        }
      } catch (error) {
        // Error fetching operators
      }
    };

    fetchOperators();
  }, [role]);

  const validate = () => {
    const nextErrors = {};
    let valid = true;

    if (!code || typeof code !== 'string' || code.trim().length === 0) {
      nextErrors.code = 'Code name is required.';
      valid = false;
    }

    if (!description || typeof description !== 'string' || description.trim().length === 0) {
      nextErrors.description = 'Description is required.';
      valid = false;
    } else if (description.trim().length > 40) {
      nextErrors.description = 'Description must be 40 characters or less.';
      valid = false;
    }

    if (!type || typeof type !== 'string' || type.trim().length === 0) {
      nextErrors.type = 'Type is required.';
      valid = false;
    }

    if (!status || !['ACTIVE', 'INACTIVE', 'EXPIRED'].includes(status)) {
      nextErrors.status = 'Status must be ACTIVE, INACTIVE, or EXPIRED.';
      valid = false;
    }

    const numericValidity = Number(validityPeriodDays);
    if (!validityPeriodDays
      || Number.isNaN(numericValidity)
      || !Number.isInteger(numericValidity)
      || numericValidity <= 0
    ) {
      nextErrors.validityPeriodDays = 'Validity period days must be a positive integer.';
      valid = false;
    }

    const numericApplicationLimit = Number(applicationLimit);
    if (!applicationLimit
      || Number.isNaN(numericApplicationLimit)
      || !Number.isInteger(numericApplicationLimit)
      || numericApplicationLimit <= 0
    ) {
      nextErrors.applicationLimit = 'Application limit must be a positive integer.';
      valid = false;
    }

    const numericDiscount = Number(discountPercentage);
    if (
      discountPercentage === ''
      || Number.isNaN(numericDiscount)
      || !Number.isInteger(numericDiscount)
      || numericDiscount < 0
      || numericDiscount > 100
    ) {
      nextErrors.discountPercentage = 'Discount percentage must be an integer between 0 and 100.';
      valid = false;
    }

    const zoneIdArray = applicableZonesText
      .split(',')
      .map((z) => z.trim())
      .filter((z) => z.length > 0);

    if (zoneIdArray.length === 0) {
      nextErrors.applicableZones = 'At least one zone must be provided.';
      valid = false;
    }

    if (!assignedOperators || (Array.isArray(assignedOperators)
      && assignedOperators.length === 0)) {
      nextErrors.assignedOperators = 'At least one operator must be assigned.';
      valid = false;
    }

    setErrors(nextErrors);
    return valid;
  };

  const isFormInvalid = () => {
    if (!code || !description || !type || !status || !validityPeriodDays || !applicationLimit || discountPercentage === '' || !applicableZonesText || !assignedOperators || (Array.isArray(assignedOperators) && assignedOperators.length === 0)) {
      return true;
    }

    // Validate numeric fields
    const numericValidity = Number(validityPeriodDays);
    if (Number.isNaN(numericValidity)
      || !Number.isInteger(numericValidity)
      || numericValidity <= 0) {
      return true;
    }

    const numericApplicationLimit = Number(applicationLimit);
    if (Number.isNaN(numericApplicationLimit)
      || !Number.isInteger(numericApplicationLimit)
      || numericApplicationLimit <= 0) {
      return true;
    }

    const numericDiscount = Number(discountPercentage);
    if (Number.isNaN(numericDiscount)
      || !Number.isInteger(numericDiscount)
      || numericDiscount < 0
      || numericDiscount > 100) {
      return true;
    }

    // Validate zones
    const zoneIdArray = applicableZonesText
      .split(',')
      .map((z) => z.trim())
      .filter((z) => z.length > 0);
    if (zoneIdArray.length === 0) {
      return true;
    }

    // Check if form is unchanged
    if (promoCode) {
      // Check if Assigned Operators or Applicable Zones have changed
      const originalZones = (Array.isArray(promoCode.applicableZones)
        ? promoCode.applicableZones : []).sort();
      const originalOperators = (Array.isArray(promoCode.assignedOperators)
        ? promoCode.assignedOperators : []).sort();
      const currentZones = zoneIdArray.sort();
      const currentOperators = (Array.isArray(assignedOperators) ? assignedOperators : []).sort();

      const zonesChanged = JSON.stringify(currentZones)
      !== JSON.stringify(originalZones);
      const operatorsChanged = JSON.stringify(currentOperators)
      !== JSON.stringify(originalOperators);

      // If either zones or operators changed, allow the button to be enabled
      if (zonesChanged || operatorsChanged) {
        return false;
      }

      // Otherwise, check if all other fields are unchanged
      const isUnchanged = code === promoCode.code
        && description === promoCode.description
        && type === promoCode.type
        && status === promoCode.status
        && Number(validityPeriodDays) === promoCode.validityPeriodDays
        && Number(applicationLimit) === promoCode.applicationLimit
        && Number(discountPercentage) === promoCode.discountPercentage;
      if (isUnchanged) {
        return true;
      }
    }

    return false;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    try {
      const selectedOperatorId = Cookies.get('selectedOperatorId');
      const zoneIdArray = applicableZonesText
        .split(',')
        .map((z) => z.trim())
        .filter((z) => z.length > 0);

      const payload = {
        code: code.trim(),
        description: description.trim(),
        type: type.trim(),
        status,
        validityPeriodDays: Number(validityPeriodDays),
        applicationLimit: Number(applicationLimit),
        discountPercentage: Number(discountPercentage),
        applicableZones: zoneIdArray,
        assignedOperators: Array.isArray(assignedOperators) ? assignedOperators : [],
      };

      const result = await APIUpdatePromoCode(
        () => {},
        promoCode.code,
        payload,
        role,
        selectedOperatorId,
      );
      if (result.status === 200) {
        setVariant('success');
        setMainMsg('Promo Code Updated Successfully');
        setSuccMsg(result.data?.message || 'Promo code has been updated successfully.');
        setErrMsg('');
        setOpen(true);
        setModalTriggerAPICalled(true);
        handleCloseMorePromoCodes();
      } else {
        const response = result?.response;
        const message = response?.data?.message || result?.message || 'Unable to update promo code.';
        setVariant('error');
        setMainMsg('Failed to update promo code');
        setErrMsg(message);
        setSuccMsg('');
        setOpen(true);
      }
    } catch (error) {
      const response = error?.response;
      const message = response?.data?.message || error?.message || 'Unable to update promo code.';
      setVariant('error');
      setMainMsg('Failed to update promo code');
      setErrMsg(message);
      setSuccMsg('');
      setOpen(true);
    }
  };

  const fontHeaderStyle = {
    color: theme.palette.grey[700],
    fontWeight: 'fontWeightMedium',
    mr: 5,
    minWidth: '250px',
  };

  const formControlStyle = {
    mb: 3,
    display: 'flex',
    flexDirection: 'row',
  };

  const buttonStyle = {
    border: '1px solid',
    borderColor: 'grey.300',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    color: theme.palette.grey[700],
    backgroundColor: 'background.paper',
    '&:hover': {
      bgcolor: alpha(theme.palette.grey[500], 0.08),
    },
  };

  const confirmButtonStyle = {
    border: 'none',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    bgcolor: theme.palette.primary.main,
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.90),
    },
    color: 'white',
    fontWeight: 'fontWeightMedium',
    '&.Mui-disabled': {
      bgcolor: theme.palette.primary.main,
      color: 'white',
      opacity: '0.6',
    },
  };

  return (
    <>
      <Box sx={{ mt: 2 }} />
      <BackButton back={handleCloseMorePromoCodes} type="promoCode" />
      <Box sx={{
        mt: 2, pb: 4, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
      }}
      >
        <Box>
          <Typography variant="h4">Edit Promo Code</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'right' }}>
          <Button sx={buttonStyle} onClick={() => handleCloseMorePromoCodes()}>
            <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
              Cancel
            </Typography>
          </Button>
          <Button sx={confirmButtonStyle} disabled={isFormInvalid()} onClick={handleSubmit}>
            <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
              Confirm
            </Typography>
          </Button>
        </Box>
      </Box>

      <Box sx={{
        padding: 1, borderTop: '1px solid', borderColor: theme.palette.grey[300], display: 'flex',
      }}
      >
        <Box sx={{ width: '70%', mt: 2 }}>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Code Name
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="Code Name"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              error={Boolean(errors.code)}
              helperText={errors.code}
              sx={{ flexGrow: 1 }}
              disabled
            />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Description
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={Boolean(errors.description)}
              helperText={errors.description || 'Max 40 characters'}
              sx={{ flexGrow: 1 }}
              inputProps={{ maxLength: 40 }}
            />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Type
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="Type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              error={Boolean(errors.type)}
              helperText={errors.type}
              sx={{ flexGrow: 1 }}
            />
          </FormControl>
          <Box sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Status
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                sx={{ flexGrow: 1 }}
              >
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                <MenuItem value="EXPIRED">EXPIRED</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Validity Period (Days)
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="Validity Period (Days)"
              type="number"
              value={validityPeriodDays}
              onChange={(e) => setValidityPeriodDays(e.target.value)}
              error={Boolean(errors.validityPeriodDays)}
              helperText={errors.validityPeriodDays}
              sx={{ flexGrow: 1 }}
            />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Application Limit
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="Application Limit"
              type="number"
              value={applicationLimit}
              onChange={(e) => setApplicationLimit(e.target.value)}
              error={Boolean(errors.applicationLimit)}
              helperText={errors.applicationLimit}
              sx={{ flexGrow: 1 }}
            />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Discount Percentage (%)
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="Discount Percentage (%)"
              type="number"
              value={discountPercentage}
              onChange={(e) => setDiscountPercentage(e.target.value)}
              error={Boolean(errors.discountPercentage)}
              helperText={errors.discountPercentage || '0-100'}
              sx={{ flexGrow: 1 }}
              inputProps={{ min: 0, max: 100 }}
            />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Applicable Zones
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              label="Applicable Zones"
              value={applicableZonesText}
              onChange={(e) => setApplicableZonesText(e.target.value)}
              error={Boolean(errors.applicableZones)}
              helperText={errors.applicableZones || 'Comma-separated zone IDs. Example: ZONE_A, ZONE_B'}
              sx={{ flexGrow: 1 }}
            />
          </FormControl>
          <Box sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Assign Operator(s)
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <FormControl fullWidth>
              <Autocomplete
                multiple
                id="assign-operators"
                options={operatorsList}
                disableCloseOnSelect
                disabled={role !== 101 && role !== 900}
                getOptionLabel={(option) => option.operatorName}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                value={
                  Array.isArray(assignedOperators)
                    ? operatorsList.filter((op) => assignedOperators.includes(op._id))
                    : []
                }
                onChange={(event, newValue) => {
                  const newOperatorIds = newValue.map((op) => op._id);
                  setAssignedOperators(newOperatorIds);
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      sx={{ mr: 1 }}
                      checked={selected}
                    />
                    <Typography variant="body2">{option.operatorName}</Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned Operators"
                    placeholder="Select operators"
                    variant="outlined"
                    fullWidth
                    error={Boolean(errors.assignedOperators)}
                    helperText={errors.assignedOperators}
                  />
                )}
                sx={{
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.paper',
                    borderRadius: 1,
                  },
                  '& .MuiChip-root': {
                    bgcolor: 'primary.light',
                    color: 'white',
                    fontWeight: 500,
                  },
                }}
              />
            </FormControl>
          </Box>
        </Box>
      </Box>
    </>
  );
}
